import { __ } from '@wordpress/i18n';
import { Freemius_Google_Ads } from './google-ads/class.freemius-google-ads';
import { Freemius_Google_Analytics } from './google-analytics/class.freemius-google-analytics';
import { IFI_Freemius_Checkout_Settings, IFreemius_Checkout_Option, IFreemius_Checkout_Handler_Settings, Freemius_Checkout_Trial_Type, IFreemius_Purchase_Response } from './interface.freemius-checkout';

/**
 * Implements the scripts for the Freemius Checkout integration.
 *
 * @since 0.4.0.210223
 */
export class Freemius_Checkout {
	/**
	 * The singleton instance of this class.
	 *
	 * @var Freemius_Checkout
	 */
	protected static _instance: Freemius_Checkout;

	/**
	 * The settings passed to the class.
	 *
	 * @var IFI_Freemius_Checkout_Settings
	 */
	protected settings: IFI_Freemius_Checkout_Settings;

	/**
	 * The instance of the Freemius Checkout Handler that will handle the checkout process.
	 *
	 * @var object
	 * @since 0.5.3.210324
	 * @link https://freemius.com/help/documentation/selling-with-freemius/freemius-checkout-buy-button/
	 */
	protected _fs_checkout_handler: any;

	/**
	 * Constructor.
	 *
	 * @param settings IFI_Freemius_Checkout_Settings
	 */
	public constructor(settings: IFI_Freemius_Checkout_Settings) {
		this.settings = settings;

		// Set some defaults for the integrations settings, to ensure that the properties
		// are always set
		// @since 0.8.7.211026
		this.settings.integrations = {...{
				google_analytics_integration: 'disabled',
				facebook_integration: false,
				// @since 0.8.8.220207
				google_ads_integration: 'disabled',
				google_ads_conversion_id: '',
			}, ...this.settings.integrations
		}

		// Store the instance of the class, so that it can be fetched
		// later by calling Edit_Product.instance()
		Freemius_Checkout._instance = this;

		this.init_checkout_widgets();
	}

	/**
	 * Returns the instance of this class.
	 *
	 * @returns Edit_Product
	 */
	public static instance(): Freemius_Checkout {
		// If the class has not been instantiated, throw an exception. The handler must be
		// initialised first, as it needs a couple of settings to get ready.
		// @since 2.0.0.201201
		if(!Freemius_Checkout._instance) {
			throw new Error('Class "Freemius_Checkout" has not been instantiated. Please call `Freemius_Checkout.init(<settings>)` before calling `Freemius_Checkout.instance()`.');
		}
		return Freemius_Checkout._instance;
	}

	/**
	 * Initialises the class and its event handlers.
	 *
	 * @param settings IFI_Freemius_Checkout_Settings
	 * @return void
	 */
	public static init(settings: IFI_Freemius_Checkout_Settings): Freemius_Checkout {
		// Initialise the Edit_Product handler. We can't call Edit_Product.instance here,
		// as the handler must be initialised with the settings first
		return new Freemius_Checkout(settings);
	}

	/**
	 * Returns the option selected in the given widget.
	 *
	 * @param widget_id
	 * @return IFreemius_Checkout_Option
	 */
	protected get_selected_option(widget_id: string): IFreemius_Checkout_Option {
		// Use the widget ID to identify the correct widget. This will allow to have multiple
		// widgets on the page and fetch the data from the one to which the "buy" button belongs
		const $widget = jQuery(`#${widget_id}`);

		const freemius_checkout_options = $widget.data('freemius_checkout_options');

		// Fetch the key that identifies the selected option. The key is made of two parts, the frequency
		// (e.g. monthly, annual, lifetime) and a unique ID, separate by a slash
		const selected_option_key = $widget.find('.checkout_options').val().split('/');

		// Fetch the Freemius data for the selected option. We need to split the key in the two parts,
		// frequency and unique ID, to fetch the data from the main object stored with the widget
		const selected_option = freemius_checkout_options[selected_option_key[0]][selected_option_key[1]];

		return selected_option;
	}

	/**
	 * Update thes the content of a checkout content, showing the detaild of the selected option.
	 *
	 * @param widget_id
	 * @since 0.7.0.210419
	 */
	protected update_widget_content(widget_id: string): void {
		const selected_option = this.get_selected_option(widget_id);

		// Load the object holding the localisation text
		const l10n = this.settings.l10n.freemius_checkout[selected_option.frequency];

		const $widget = jQuery(`#${widget_id}`);
		$widget.find('.price').html(selected_option.price_html);
		$widget.find('.recurrence').html(l10n.recurrence);
		$widget.find('.checkout_option_highlights .sites_item .sites').html(selected_option.sites_html);
		$widget.find('.checkout_option_highlights .updates_item .updates').text(l10n.updates).attr('title', l10n.updates_description);
	}

	/**
	 * Given a Freemius Checkout Option, it returns the settings to initialise the Freemius Checkout Handler.
	 *
	 * @param freemius_checkout_option A checkout option, from which the settings will be derived.
	 * @param trial_type
	 * @returns IFreemius_Checkout_Handler_Settings
	 * @since 0.7.0.210419
	 */
	protected generate_freemius_checkout_settings(freemius_checkout_option: IFreemius_Checkout_Option, trial_type: Freemius_Checkout_Trial_Type): IFreemius_Checkout_Handler_Settings {
		const fs_checkout_settings: IFreemius_Checkout_Handler_Settings = {
			// Required product ID (whether it's a plugin, theme, add-on, bundle, or SaaS).
			plugin_id: freemius_checkout_option.plugin_id,
			// Required product public key.
			public_key: freemius_checkout_option.public_key,

			// The ID of the plan that will load with the checkout. When selling multiple plans you can set the param when calling the open() method.
			// Defaults to the 1st paid plan.
			plan_id: freemius_checkout_option.plan_id,
			// A multi-site licenses prices that will load immediately with the checkout. A developer-friendly param that can be used instead of the pricing_id. To specify unlimited licenses prices, use one of the following values: 0, null, or 'unlimited'.
			// Default is 1
			licenses: freemius_checkout_option.licenses,

			// Use the licenses param instead. An optional ID of the exact multi-site license prices that will load once the checkout opened.
			// Defaults to the plan's single-site prices ID.
			pricing_id: freemius_checkout_option.pricing_id,

			// An optional billing cycle that will be auto selected when the checkout is opened. Can be one of the following values: 'monthly', 'annual', 'lifetime'.
			// Default is 'annual'
			billing_cycle: freemius_checkout_option.frequency,

			// One of the following 3-chars currency codes (ISO 4217): 'usd', 'eur', 'gbp'.
			// Default is 'usd'
			// Note: ISO 4217 currency codes are actually UPPER-CASE (they are case-sensitive). However, Freemius expects them to be lower-case.
			currency: freemius_checkout_option.currency.toLowerCase(),

			// When set to true, it will open the checkout in a trial mode and the trial type (free vs. paid) will be based on the plan's configuration. This will only work if you've activated the Free Trial functionality in the plan configuration. If you configured the plan to support a trial that doesn't require a payment method, you can also open the checkout in a trial mode that requires a payment method by setting the value to 'paid'.
			// Default is false
			trial: trial_type,

			// An optional coupon code to be automatically applied on the checkout immediately when opened.
			// TODO Add support for coupons
			//coupon?: string;

			// An optional ID to set the id attribute of the checkout's <body> HTML element. This argument is particularly useful if you have multiple checkout instances that need to have a slightly different design or visibility of UI components. You can assign a unique ID for each instance and customize it differently using the CSS stylesheet that you can attach through the PLANS -> CUSTOMIZATION in the Developer Dashboard.
			//id: string;
			// An optional string to override the product's title.
			// Defaults to the product's title set within the Freemius dashboard.
			//name?: string;

			// An optional string to override the checkout's title when buying a new license.
			// Defaults to "Great selection, {{ firstName }}!"
			// TODO Allow to enter a checkout title in the integration settings
			//title?: string;
			// An optional string to override the checkout's subtitle.
			// Defaults to "You're one step closer to our {{ planTitle }} features"
			// TODO Allow to enter a checkout title in the integration settings
			// TODO Show "you're about to start your free trial" when the trial option is selected
			//subtitle?: string;

			// An optional icon that loads at the checkout and will override the product's icon uploaded to the Freemius Dashboard. Use a secure path to the image over HTTPS. While the checkout will remain PCI compliant, credit-card automatic prefill by the browser will not work.
			// Defaults to the product's title set within the Freemius dashboard.
			// TODO Add an option to allow fetching the image from the product in WooCommerce
			//image?: string;
			// Set this param to true if you like to disable the licenses selector when the product is sold with multiple license activation options.
			// Default is false
			//disable_licenses_selector?: boolean;

			// Callbacks
			// An callback called after a successful purchase/subscription completion .
			// @since 0.8.6.211023
			purchaseCompleted: (response: IFreemius_Purchase_Response) => {
				this.handle_purchase_event(response, freemius_checkout_option);
			},


			// TODO Add support for renewing an existing licence. This could be useful when migrating the product from an existing licensing system
			// An optional param to pre-populate a license key for license renewal, license extension and more.
			//license_key?: string;
			// An optional string to prefill the buyer's email address.
			//user_email?: string;
			// An optional string to prefill the buyer's first name.
			//user_firstname?: string;
			// An optional string to prefill the buyer's last name.
			//user_lastname?: string;

			// An optional user ID to associate purchases generated through the checkout with their affiliate account.
			// TODO Add support for affiliates
			//affiliate_user_id?: number;

			// Show or hide the currency selector on the checkout page
			// @since 0.10.0.241126
			show_inline_currency_selector: false,
		}

		// Add the sandbox mode data, if that option is enabled
		if(freemius_checkout_option.sandbox_token) {
			fs_checkout_settings.sandbox = freemius_checkout_option.sandbox_token;
			fs_checkout_settings.timestamp = freemius_checkout_option.timestamp;
		}

		console.log(fs_checkout_settings, this.settings);

		return fs_checkout_settings;
	}

	/**
	 * Handles a "purchase completed" event sent by Freemius. This method sends the purchase
	 * details to services like Google Analytics and Facebook, if such integrations are enabled.
	 *
	 * @param freemius_response The response returned by Freemius.
	 * @param freemius_checkout_option The purchased option.
	 * @return void
	 * @since 0.8.6.211023
	 * @see Freemius_Google_Analytics
	 */
	protected handle_purchase_event(freemius_response: IFreemius_Purchase_Response, freemius_checkout_option: IFreemius_Checkout_Option): void {
		// Load the Google Analytics integration, if enabled
		if(this.settings.integrations.google_analytics_integration === 'gtag') {
			// If the Google Tag script is not loaded, stop here. The tracking could not be performed, anyway
			// @since 0.8.7.211026
			if(!gtag) {
				console.log([
					__('The Google Analytics integration for Freemius Checkout could not be loaded.', this.settings.text_domain),
					__('The Google Tag Manager (gtag.js) could not be found. Please make sure that you installed Google Analytics scripts correctly.', this.settings.text_domain),
				].join(' '));
				return;
			}

			// Initialise the Google Analytics integration
			const google_analytics_integration = new Freemius_Google_Analytics({
				integration_type: this.settings.integrations.google_analytics_integration,
				text_domain: this.settings.text_domain,
				store: this.settings.store,
			});

			// Track the purchase
			google_analytics_integration.track_purchase(freemius_response, freemius_checkout_option);
		}

		// Load the Google Ads integration, if enabled
		// @since 0.8.8.220207
		if(this.settings.integrations.google_ads_integration === 'gtag') {
			// If the Google Tag script is not loaded, stop here. The tracking could not be performed, anyway
			// @since 0.8.8.220207
			if(!gtag) {
				console.log([
					__('The Google Ads integration for Freemius Checkout could not be loaded.', this.settings.text_domain),
					__('The Google Tag Manager script (gtag.js) could not be found. Please make sure that you installed Google Analytics scripts correctly.', this.settings.text_domain),
				].join(' '));
				return;
			}

			// Initialise the Google Ads integration
			const google_ads_integration = new Freemius_Google_Ads({
				integration_type: this.settings.integrations.google_ads_integration,
				text_domain: this.settings.text_domain,
				store: this.settings.store,
				// @since 0.8.7.211026
				google_ads_conversion_id: this.settings.integrations.google_ads_conversion_id,
			});

			// Track the conversion
			// @since 0.8.7.211026
			google_ads_integration.track_conversion(freemius_response, freemius_checkout_option);
		}
	}

	/**
	 * Starts the Freemius checkout process.
	 *
	 * @param widget_id
	 * @param trial_type
	 * @since 0.7.0.210419
	 * @link https://freemius.com/help/documentation/selling-with-freemius/freemius-checkout-buy-button/#freemius_checkout_buy_button_javascript_api
	 */
	protected start_checkout(widget_id: string, trial_type: Freemius_Checkout_Trial_Type = false): void {
		const selected_option = this.get_selected_option(widget_id);

		// Initialise the new Freemius Checkout SDK
		// @since 0.10.0.241126
		const fs_checkout_handler = new FS.Checkout(this.generate_freemius_checkout_settings(selected_option, trial_type));
		fs_checkout_handler.open();
	}

	/**
	 * Initialises the checkout interface.
	 */
	protected init_checkout_widgets() {
		// Intercept the selection of a checkout option, to update the UI
		jQuery(document).on('change', '.freemius_checkout_widget .checkout_options', (event) => {
			event.preventDefault();

			// Update the widget content, showing the selected option
			this.update_widget_content(jQuery(event.target).data('widget_id'));
		});

		// Intercept the click on the "purchase" action, to initialise the purchase process
		jQuery(document).on('click', '.freemius_checkout_widget .buy_product', (event) => {
			event.preventDefault();

			// Starts the Freemius checkout process
			this.start_checkout(jQuery(event.target).data('widget_id'));
		});

		// Intercept the click on the "trial" action, to initialise the process to get a trial version of the product
		// @since 0.7.0.210419
		jQuery(document).on('click', '.freemius_checkout_widget .freemius_checkout_free_trial', (event) => {
			event.preventDefault();

			// Starts the Freemius checkout process
			this.start_checkout(jQuery(event.target).data('widget_id'), true);
		});

		// Trigger a "change" event on all the checkout options list, to update the widgets on the page
		jQuery('.freemius_checkout_widget .checkout_options').trigger('change');
	}
}